import attributeService from "services/attributeService";
import RestAPIClient from "services/RestAPIClient";
import { IAttribute, IPageableData } from "services/typings";
import permissionService from "./permissionService";
import {
  IUpdatePasswordRequest,
  IUser,
  IUserConfig,
  IUserConfigInfor,
  IUserConfigUpdation,
  IUserCreation,
  IUserInfo,
  IUserInfoUpdation,
  IUserRequest,
  IUserUpdation,
  IUserIntegrationUpdation,
  IUserIntegration,
} from "./typings";

class UserService extends RestAPIClient {
  constructor() {
    super("users");
  }

  getUsers = async (req: IUserRequest): Promise<IPageableData<IUser>> => {
    const { columns, filters, ...rest } = req;
    const res = await this.post(
      `/admin?${this.querystring.stringify(rest as any)}`,
      {
        columns,
        filters,
      }
    );
    return {
      data: res.data,
      total: res.pagination.total,
    };
  };

  persistUser = (user: IUser) => {
    localStorage.setItem("USER", JSON.stringify(user));
  };

  getPersistedUser = (): IUser => {
    const res = localStorage.getItem("USER");
    return !!res ? JSON.parse(res) : undefined;
  };

  getUser = async (id: string): Promise<IUser> => {
    const res = await this.get(`/admin/${id}`);
    return res.data;
  };
  create = async (req: IUserCreation): Promise<IUser> => {
    const res = await this.post("/admin/create", req);
    return res.data;
  };

  update = async (req: IUserUpdation): Promise<IUser> => {
    const res = await this.put(`/admin/${req.id}`, req);
    return res.data;
  };

  getInfo = async (): Promise<IUserInfo> => {
    const res = await this.get("/me/info");
    return res.data;
  };

  updateInfo = async (req: IUserInfoUpdation): Promise<IUserInfo> => {
    const res = await this.put("/me/info", req);
    return res.data;
  };

  updateBillingInfo = async (req: IUserInfoUpdation): Promise<IUserInfo> => {
    const res = await this.put("/me/billing", req);
    return res.data;
  };

  updatePassword = async (req: IUpdatePasswordRequest): Promise<null> => {
    await this.put("/me/password", req);
    return null;
  };

  getUserAttributes = async (): Promise<IAttribute[]> => {
    return attributeService.getAttributes({ kind: "user" });
  };

  getCurrentUser = async (opts?: {
    token?: string;
    sellerId?: number;
  }): Promise<IUser> => {
    const data = (
      await this.get(
        "/me",
        opts?.sellerId ? { seller_id: opts?.sellerId } : undefined,
        opts
      )
    ).data;
    return {
      ...(data || {}),
      role: {
        ...(data?.role || {}),
        permissions: data?.role?.permissions?.reduce(
          (acc: any, { action, resource }) => {
            acc[`${resource}:${action}`] = true;
            return acc;
          },
          {}
        ),
      },
    };
  };

  search = async (text: string): Promise<IUser[]> => {
    const res = await this.get(`/q`, { username: text });
    return res.data;
  };

  getConfig = async (): Promise<IUserConfig> => {
    const res = await this.get("/me/config");
    return res.data;
  };

  updateConfig = async (req: IUserConfigUpdation): Promise<IUserConfig> => {
    const res = await this.put("/me/config", req);
    return res.data;
  };

  getConfigInfo = async (): Promise<IUserConfigInfor> => {
    const res = await this.get("/me/config/campaign-info");
    return res.data;
  };

  getIntegration = async (): Promise<IUserIntegration> => {
    const res = await this.get("/me/integration");
    return res.data;
  };

  updateIntegration = async (
    req: IUserIntegrationUpdation
  ): Promise<{ success: boolean; error: any }> => {
    const res = await this.put("/me/integration", req);
    return res.data;
  };

  getIntegrationToken = async (): Promise<{ token: string }> => {
    const res = await this.get("/me/integration/token");
    return res.data;
  };
}

export default new UserService();
